import React, { useState, useEffect, useCallback } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API_BASE = `${process.env.REACT_APP_API_URL}/api/airport`;
const HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
};

const emptyCarForm = { name: '', details: '', category: '', capacity: '', rate: '' };
const emptyNoteForm = { title: '', body: '' };

function AirportAdmin() {
  const [cars, setCars] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cars');

  // Car form
  const [carForm, setCarForm] = useState(emptyCarForm);
  const [editingCarId, setEditingCarId] = useState(null);
  const [editingCarImageURL, setEditingCarImageURL] = useState(''); // existing Firebase URL when editing
  const [showCarForm, setShowCarForm] = useState(false);
  const [carSaving, setCarSaving] = useState(false);
  const [carMsg, setCarMsg] = useState(null);

  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Note form
  const [noteForm, setNoteForm] = useState(emptyNoteForm);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMsg, setNoteMsg] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [carsRes, notesRes] = await Promise.all([
        fetch(`${API_BASE}/cars`, { headers: HEADERS, mode: 'cors' }),
        fetch(`${API_BASE}/notes`, { headers: HEADERS, mode: 'cors' }),
      ]);
      const carsData = await carsRes.json();
      const notesData = await notesRes.json();
      if (carsData.success) setCars(carsData.data);
      if (notesData.success) setNotes(notesData.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── IMAGE HANDLER ──
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // ── CAR HANDLERS ──
  const openAddCar = () => {
    setCarForm(emptyCarForm);
    setEditingCarId(null);
    setEditingCarImageURL('');
    setImageFile(null);
    setImagePreview('');
    setCarMsg(null);
    setShowCarForm(true);
  };

  const openEditCar = (car) => {
    setCarForm({
      name: car.name || '',
      details: car.details || '',
      category: car.category || '',
      capacity: car.capacity || '',
      rate: car.rate || '',
    });
    setEditingCarId(car._id);
    setEditingCarImageURL(car.imageURL || '');
    setImageFile(null);
    setImagePreview(car.imageURL || ''); // show existing image in preview
    setCarMsg(null);
    setShowCarForm(true);
  };

  const saveCar = async () => {
    // Require an image file when adding a new car
    if (!editingCarId && !imageFile) {
      setCarMsg({ type: 'error', text: 'Please select an image for the car.' });
      return;
    }

    setCarSaving(true);
    setCarMsg(null);

    try {
      let imageURL = editingCarImageURL; // keep existing URL if editing with no new file

      // Upload to Firebase Storage if admin picked a new file
      if (imageFile) {
        const imageRef = ref(storage, `airport/images/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      const payload = { ...carForm, imageURL };

      const url    = editingCarId ? `${API_BASE}/cars/${editingCarId}` : `${API_BASE}/cars`;
      const method = editingCarId ? 'PUT' : 'POST';

      const res  = await fetch(url, { method, headers: HEADERS, mode: 'cors', body: JSON.stringify(payload) });
      const data = await res.json();

      if (data.success) {
        setCarMsg({ type: 'success', text: editingCarId ? 'Car updated!' : 'Car added!' });
        setShowCarForm(false);
        fetchAll();
      } else {
        setCarMsg({ type: 'error', text: data.message || 'Error saving car' });
      }
    } catch (e) {
      setCarMsg({ type: 'error', text: 'Error: ' + e.message });
    } finally {
      setCarSaving(false);
    }
  };

  const deleteCar = async (id) => {
    const car = cars.find(c => c._id === id);
    try {
      // Delete from Firebase Storage if it's a Firebase URL
      if (car?.imageURL && car.imageURL.includes('firebasestorage')) {
        await deleteObject(ref(storage, car.imageURL)).catch(err => {
          if (err.code !== 'storage/object-not-found') console.warn('Image delete warning:', err);
        });
      }
      await fetch(`${API_BASE}/cars/${id}`, { method: 'DELETE', headers: HEADERS, mode: 'cors' });
      fetchAll();
    } catch (e) {
      console.error(e);
    }
    setDeleteConfirm(null);
  };

  // ── NOTE HANDLERS ──
  const openAddNote = () => {
    setNoteForm(emptyNoteForm);
    setEditingNoteId(null);
    setNoteMsg(null);
    setShowNoteForm(true);
  };

  const openEditNote = (note) => {
    setNoteForm({ title: note.title || '', body: note.body || '' });
    setEditingNoteId(note._id);
    setNoteMsg(null);
    setShowNoteForm(true);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    setNoteMsg(null);
    try {
      const url    = editingNoteId ? `${API_BASE}/notes/${editingNoteId}` : `${API_BASE}/notes`;
      const method = editingNoteId ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers: HEADERS, mode: 'cors', body: JSON.stringify(noteForm) });
      const data   = await res.json();
      if (data.success) {
        setNoteMsg({ type: 'success', text: editingNoteId ? 'Note updated!' : 'Note added!' });
        setShowNoteForm(false);
        fetchAll();
      } else {
        setNoteMsg({ type: 'error', text: data.message || 'Error saving note' });
      }
    } catch (e) {
      setNoteMsg({ type: 'error', text: 'Network error' });
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await fetch(`${API_BASE}/notes/${id}`, { method: 'DELETE', headers: HEADERS, mode: 'cors' });
      fetchAll();
    } catch (e) { console.error(e); }
    setDeleteConfirm(null);
  };

  // ── STYLES ──
  const s = {
    page: { minHeight: '100vh', background: '#F8F9FA', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1A1A2E' },
    header: { background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '0 clamp(1rem,4vw,2.5rem)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
    headerTitle: { fontSize: 'clamp(1.1rem,3vw,1.3rem)', fontWeight: '700', color: '#1A1A2E', display: 'flex', alignItems: 'center', gap: '0.6rem' },
    body: { maxWidth: '1400px', margin: '0 auto', padding: 'clamp(1.5rem,4vw,2.5rem) clamp(1rem,4vw,2.5rem)' },
    tabs: { display: 'flex', gap: '0.5rem', marginBottom: '2rem', background: '#FFFFFF', padding: '0.4rem', borderRadius: '10px', border: '1px solid #E5E7EB', width: 'fit-content' },
    tab: (active) => ({ padding: '0.55rem 1.4rem', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.2s', background: active ? '#1A1A2E' : 'transparent', color: active ? '#FFFFFF' : '#6B7280' }),
    sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
    sectionTitle: { fontSize: 'clamp(1.2rem,3vw,1.5rem)', fontWeight: '700', color: '#1A1A2E' },
    addBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.4rem', background: '#1A1A2E', color: '#FFFFFF', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '1.5rem' },
    card: { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    cardImg: { width: '100%', height: '200px', objectFit: 'cover', display: 'block' },
    cardImgPlaceholder: { width: '100%', height: '200px', background: 'linear-gradient(135deg, #F3F4F6, #E5E7EB)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '2.5rem' },
    cardBody: { padding: '1.25rem' },
    cardName: { fontSize: '1.05rem', fontWeight: '700', color: '#1A1A2E', marginBottom: '0.4rem' },
    badge: (color) => ({ display: 'inline-block', padding: '0.25rem 0.7rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: color === 'blue' ? '#EFF6FF' : '#F0FDF4', color: color === 'blue' ? '#1D4ED8' : '#15803D', marginRight: '0.4rem', marginBottom: '0.4rem' }),
    cardMeta: { fontSize: '0.82rem', color: '#6B7280', marginTop: '0.5rem', lineHeight: '1.6' },
    cardRate: { fontSize: '1.1rem', fontWeight: '700', color: '#059669', marginTop: '0.5rem' },
    cardActions: { display: 'flex', gap: '0.5rem', marginTop: '1rem' },
    editBtn: { flex: 1, padding: '0.55rem', background: '#F9FAFB', color: '#374151', border: '1px solid #E5E7EB', borderRadius: '7px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' },
    deleteBtn: { flex: 1, padding: '0.55rem', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: '7px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' },
    noteCard: { background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.4rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    noteTitle: { fontSize: '1rem', fontWeight: '700', color: '#1A1A2E', marginBottom: '0.5rem' },
    noteBody: { fontSize: '0.88rem', color: '#6B7280', lineHeight: '1.7', whiteSpace: 'pre-wrap' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
    modal: { background: '#FFFFFF', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    modalHeader: { padding: '1.5rem 1.5rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '1.2rem', fontWeight: '700', color: '#1A1A2E' },
    closeBtn: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280', lineHeight: 1 },
    modalBody: { padding: '1.25rem 1.5rem 1.5rem' },
    label: { display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' },
    input: { width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.9rem', color: '#1A1A2E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '0.65rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.9rem', color: '#1A1A2E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', minHeight: '100px', resize: 'vertical', fontFamily: 'inherit' },
    formGroup: { marginBottom: '1rem' },
    saveBtn: (disabled) => ({ width: '100%', padding: '0.8rem', background: disabled ? '#9CA3AF' : '#1A1A2E', color: '#FFFFFF', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', marginTop: '0.5rem' }),
    msg: (type) => ({ padding: '0.7rem 1rem', borderRadius: '8px', fontSize: '0.88rem', fontWeight: '500', marginBottom: '1rem', background: type === 'success' ? '#F0FDF4' : '#FEF2F2', color: type === 'success' ? '#15803D' : '#DC2626', border: `1px solid ${type === 'success' ? '#BBF7D0' : '#FECACA'}` }),
    imgPreview: { width: '100%', height: '180px', objectFit: 'cover', borderRadius: '8px', marginTop: '0.5rem', border: '1px solid #E5E7EB', display: 'block' },
    confirmModal: { background: '#FFFFFF', borderRadius: '16px', width: '100%', maxWidth: '380px', padding: '2rem', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  };

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6B7280' }}>
      Loading...
    </div>
  );

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTitle}>
          <span>✈️</span>
          <span>Airport Service Admin</span>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>{cars.length} cars · {notes.length} notes</div>
      </div>

      <div style={s.body}>
        {/* Tabs */}
        <div style={s.tabs}>
          <button style={s.tab(activeTab === 'cars')}  onClick={() => setActiveTab('cars')}>🚗 Cars ({cars.length})</button>
          <button style={s.tab(activeTab === 'notes')} onClick={() => setActiveTab('notes')}>📋 Notes ({notes.length})</button>
        </div>

        {/* ── CARS TAB ── */}
        {activeTab === 'cars' && (
          <>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Airport Cars</h2>
              <button style={s.addBtn} onClick={openAddCar}>+ Add Car</button>
            </div>
            {carMsg && !showCarForm && <div style={s.msg(carMsg.type)}>{carMsg.text}</div>}
            {cars.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚗</div>
                <p style={{ fontWeight: '600' }}>No cars yet. Add your first one!</p>
              </div>
            ) : (
              <div style={s.grid}>
                {cars.map(car => (
                  <div key={car._id} style={s.card}>
                    {car.imageURL
                      ? <img src={car.imageURL} alt={car.name} style={s.cardImg} onError={e => { e.target.style.display = 'none'; }} />
                      : <div style={s.cardImgPlaceholder}>🚗</div>
                    }
                    <div style={s.cardBody}>
                      <div style={s.cardName}>{car.name || '—'}</div>
                      <div>
                        {car.category && <span style={s.badge('blue')}>{car.category}</span>}
                        {car.capacity && <span style={s.badge('green')}>👥 {car.capacity}</span>}
                      </div>
                      {car.details && <div style={s.cardMeta}>{car.details}</div>}
                      {car.rate && <div style={s.cardRate}>Nu. {car.rate} + GST</div>}
                      <div style={s.cardActions}>
                        <button style={s.editBtn}   onClick={() => openEditCar(car)}>✏️ Edit</button>
                        <button style={s.deleteBtn} onClick={() => setDeleteConfirm({ type: 'car', id: car._id, name: car.name || 'this car' })}>🗑 Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── NOTES TAB ── */}
        {activeTab === 'notes' && (
          <>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Service Notes</h2>
              <button style={s.addBtn} onClick={openAddNote}>+ Add Note</button>
            </div>
            {noteMsg && !showNoteForm && <div style={s.msg(noteMsg.type)}>{noteMsg.text}</div>}
            {notes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                <p style={{ fontWeight: '600' }}>No notes yet. Add one!</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))', gap: '1.5rem' }}>
                {notes.map(note => (
                  <div key={note._id} style={s.noteCard}>
                    <div style={s.noteTitle}>{note.title || '(No title)'}</div>
                    <div style={s.noteBody}>{note.body || '(No content)'}</div>
                    <div style={{ ...s.cardActions, marginTop: '1rem' }}>
                      <button style={s.editBtn}   onClick={() => openEditNote(note)}>✏️ Edit</button>
                      <button style={s.deleteBtn} onClick={() => setDeleteConfirm({ type: 'note', id: note._id, name: note.title || 'this note' })}>🗑 Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── CAR FORM MODAL ── */}
      {showCarForm && (
        <div style={s.overlay} onClick={() => setShowCarForm(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>{editingCarId ? 'Edit Car' : 'Add New Car'}</span>
              <button style={s.closeBtn} onClick={() => setShowCarForm(false)}>×</button>
            </div>
            <div style={s.modalBody}>
              {carMsg && <div style={s.msg(carMsg.type)}>{carMsg.text}</div>}

              {[
                { key: 'name',     label: 'Car Name',   placeholder: 'e.g. AVATR' },
                { key: 'category', label: 'Category',   placeholder: 'e.g. SUV, Sedan, Van' },
                { key: 'capacity', label: 'Capacity',   placeholder: 'e.g. 4 passengers' },
                { key: 'rate',     label: 'Rate (Nu.)', placeholder: 'e.g. 2500' },
              ].map(field => (
                <div key={field.key} style={s.formGroup}>
                  <label style={s.label}>{field.label}</label>
                  <input
                    style={s.input}
                    value={carForm[field.key]}
                    onChange={e => setCarForm(f => ({ ...f, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}

              <div style={s.formGroup}>
                <label style={s.label}>Details</label>
                <textarea
                  style={s.textarea}
                  value={carForm.details}
                  onChange={e => setCarForm(f => ({ ...f, details: e.target.value }))}
                  placeholder="Additional details about the vehicle..."
                />
              </div>

              {/* ── IMAGE UPLOAD (Firebase Storage) ── */}
              <div style={s.formGroup}>
                <label style={s.label}>
                  Car Image {!editingCarId && <span style={{ color: '#DC2626' }}>*</span>}
                  {editingCarId && (
                    <span style={{ color: '#6B7280', fontWeight: '400', marginLeft: '0.3rem' }}>
                      (leave empty to keep current)
                    </span>
                  )}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ width: '100%', padding: '0.5rem 0', fontSize: '0.88rem', color: '#374151', cursor: 'pointer' }}
                />
                {/* Preview: new blob OR existing Firebase URL */}
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={s.imgPreview}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>

              <button
                style={s.saveBtn(carSaving)}
                onClick={saveCar}
                disabled={carSaving}
              >
                {carSaving
                  ? (imageFile ? '⏳ Uploading image...' : '⏳ Saving...')
                  : (editingCarId ? 'Update Car' : 'Add Car')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── NOTE FORM MODAL ── */}
      {showNoteForm && (
        <div style={s.overlay} onClick={() => setShowNoteForm(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>{editingNoteId ? 'Edit Note' : 'Add New Note'}</span>
              <button style={s.closeBtn} onClick={() => setShowNoteForm(false)}>×</button>
            </div>
            <div style={s.modalBody}>
              {noteMsg && <div style={s.msg(noteMsg.type)}>{noteMsg.text}</div>}
              <div style={s.formGroup}>
                <label style={s.label}>Note Title</label>
                <input style={s.input} value={noteForm.title} onChange={e => setNoteForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Booking Policy" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Note Body</label>
                <textarea style={{ ...s.textarea, minHeight: '140px' }} value={noteForm.body} onChange={e => setNoteForm(f => ({ ...f, body: e.target.value }))} placeholder="Write the note content here..." />
              </div>
              <button style={s.saveBtn(noteSaving)} onClick={saveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : (editingNoteId ? 'Update Note' : 'Add Note')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM ── */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={s.confirmModal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🗑️</div>
            <h3 style={{ fontWeight: '700', marginBottom: '0.5rem', color: '#1A1A2E' }}>
              Delete {deleteConfirm.type === 'car' ? 'Car' : 'Note'}?
            </h3>
            <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button style={{ flex: 1, padding: '0.75rem', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }} onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button style={{ flex: 1, padding: '0.75rem', background: '#DC2626', color: '#FFFFFF', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => deleteConfirm.type === 'car' ? deleteCar(deleteConfirm.id) : deleteNote(deleteConfirm.id)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AirportAdmin;